/**
 * 🔗 Utilitários para códigos curtos de cards
 * 
 * O shortCode é um código de 7 caracteres alfanumérico derivado do ID do card.
 * Permite compartilhar links mais amigáveis e exibir IDs mais curtos na interface.
 * 
 * Formato: 7 caracteres hexadecimais (ex: 1FB4CB3)
 */

import { LEGACY_CARD_IDS } from './legacyCardIds';

/**
 * Algoritmo NOVO de shortCode (sem colisões).
 *
 * Usa hash FNV-1a de 64 bits sobre a STRING COMPLETA do ID (incluindo letras),
 * reduzido para base36 com 7 caracteres (~78 bilhões de combinações).
 * Isso evita as colisões do algoritmo antigo, que descartava as letras do
 * ObjectId e usava apenas os últimos 7 dígitos hexadecimais.
 */
function generateShortCodeNovo(cardId: string): string {
  // FNV-1a 64 bits sobre a string completa (preserva letras a-f do ObjectId)
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const mask = 0xffffffffffffffffn;
  for (let i = 0; i < cardId.length; i++) {
    hash ^= BigInt(cardId.charCodeAt(i));
    hash = (hash * prime) & mask;
  }

  const espaco = 36n ** 7n; // ~78 bilhões de combinações
  const code = (hash % espaco).toString(36).toUpperCase();
  return code.padStart(7, '0');
}

/**
 * Gera o shortCode de exibição a partir do ID do card.
 * Determinístico — mesmo ID sempre gera o mesmo shortCode.
 *
 * Para PRESERVAR os códigos já existentes/compartilhados, qualquer card cujo id
 * já existia no momento da correção (ver legacyCardIds.ts) mantém o código ANTIGO
 * (legacy). Cards NOVOS (id fora do snapshot) usam o algoritmo novo, sem colisão.
 */
export function generateShortCode(cardId: string): string {
  if (!cardId) return '';
  if (LEGACY_CARD_IDS.has(cardId)) {
    return generateShortCodeLegacy(cardId);
  }
  return generateShortCodeNovo(cardId);
}

/**
 * ⚠️ Algoritmo ANTIGO de shortCode (hex dos dígitos do ID, últimos 7).
 * Mantido APENAS para resolver links já compartilhados antes da correção.
 * Sujeito a colisões — não use para exibição.
 */
export function generateShortCodeLegacy(cardId: string): string {
  if (!cardId) return '';

  const numericStr = cardId.replace(/\D/g, '');

  if (numericStr.length > 0) {
    try {
      const bigNum = BigInt(numericStr);
      const hexCode = bigNum.toString(16).toUpperCase();
      return hexCode.slice(-7).padStart(7, '0');
    } catch {
      // Se BigInt falhar, usa fallback
    }
  }

  let hash = 0;
  for (let i = 0; i < cardId.length; i++) {
    const char = cardId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  const positiveHash = Math.abs(hash);
  return positiveHash.toString(16).toUpperCase().slice(0, 7).padStart(7, '0');
}

/**
 * Tenta extrair o ID original a partir do shortCode
 * NOTA: Isso pode não ser 100% preciso para IDs muito grandes
 * Na prática, o backend faz a busca real pelo shortCode
 */
export function shortCodeToId(shortCode: string): string | null {
  if (!shortCode || shortCode.length < 6) return null;
  
  try {
    // Converter hex para decimal
    const numericValue = parseInt(shortCode, 16);
    return numericValue.toString();
  } catch {
    return null;
  }
}

/**
 * Formata o shortCode para exibição (ex: #1FB4CB3)
 */
export function formatShortCode(cardId: string): string {
  const code = generateShortCode(cardId);
  return `#${code}`;
}

/**
 * Gera URL de compartilhamento com shortCode
 */
export function getShareableUrl(cardId: string): string {
  const shortCode = generateShortCode(cardId);
  return `${window.location.origin}/c/${shortCode}`;
}

/**
 * Gera URL de compartilhamento com DuckDNS (para mensagens WhatsApp)
 */
export function getShareableUrlDuckDNS(cardId: string): string {
  const shortCode = generateShortCode(cardId);
  return `https://escalone.duckdns.org/c/${shortCode}`;
}

/**
 * Gera URL completa do card (com flowId e cardId)
 */
export function getFullCardUrl(flowId: string, cardId: string): string {
  return `${window.location.origin}/flows/${flowId}/card/${cardId}`;
}

/**
 * Verifica se um código é um shortCode válido
 * Aceita base36 (códigos novos: 0-9A-Z) e hex (códigos antigos), 6 a 8 caracteres
 */
export function isValidShortCode(code: string): boolean {
  if (!code || code.length < 6 || code.length > 8) return false;
  return /^[0-9A-Za-z]{6,8}$/.test(code);
}

/**
 * Extrai shortCode de uma URL /c/:shortCode
 */
export function extractShortCodeFromUrl(url: string): string | null {
  const match = url.match(/\/c\/([0-9A-Za-z]{6,8})$/i);
  return match ? match[1].toUpperCase() : null;
}
